import React, { useState, useEffect } from 'react';
import './App.css';
import CommuneSearch from './components/CommuneSearch';
import ItineraireMap from './components/ItineraireMap';
import ItineraireList from './components/ItineraireList';
import { Commune, ItinerairePoint } from './types/Commune';
import { CommuneService } from './services/CommuneService';

function App() {
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [selectedCommune, setSelectedCommune] = useState<Commune | null>(null);
  const [itineraire, setItineraire] = useState<ItinerairePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCommunes();
  }, []);

  const loadCommunes = async () => {
    try {
      setLoading(true);
      console.log('🔍 Chargement des communes...');
      const communesData = await CommuneService.loadCommunes();
      console.log('✅ Communes chargées:', communesData.length);
      setCommunes(communesData);
    } catch (err) {
      setError('Erreur lors du chargement des communes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommuneSelect = (communeId: string) => {
    console.log('🔍 Commune sélectionnée:', communeId);
    const commune = CommuneService.getCommuneById(communeId);
    if (commune) {
      console.log('✅ Commune trouvée:', commune.nom);
      console.log('📍 Coordonnées:', commune.latitude, commune.longitude);
      setSelectedCommune(commune);
      calculateItineraire(commune);
    } else {
      console.log('❌ Commune non trouvée pour ID:', communeId);
    }
  };

  const calculateItineraire = (commune: Commune) => {
    try {
      console.log('🔍 calculateItineraire appelé pour:', commune.nom);

      // Trouver toutes les communes dans un rayon de 50km
      const communesProches = CommuneService.findCommunesWithinRadius(commune, 50);
      console.log('🔍 Communes proches trouvées:', communesProches.length);

      // Calculer l'itinéraire optimal
      const itineraireOptimal = CommuneService.calculateOptimalItinerary(communesProches);
      console.log('🔍 Itinéraire optimal calculé:', itineraireOptimal.length, 'étapes');

      setItineraire(itineraireOptimal);
      setError(null);
    } catch (err) {
      setError('Erreur lors du calcul de l\'itinéraire');
      console.error(err);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🛣️ Planificateur d'Itinéraire</h1>
        <p>Trouvez le meilleur itinéraire pour visiter les communes dans un rayon de 50km</p>
      </header>

      <main className="App-main">
        {loading ? (
          <div className="loading-section">
            <div className="loading-spinner">⏳</div>
            <p>Chargement des communes en cours...</p>
          </div>
        ) : (
          <div className="search-section">
            <CommuneSearch
              communes={communes}
              onCommuneSelect={handleCommuneSelect}
              loading={loading}
            />
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {selectedCommune && (
          <div className="results-section">
            <div className="selected-commune">
              <h2>Commune sélectionnée : {selectedCommune.nom}</h2>
              <div className="commune-details">
                <p><strong>Ville :</strong> {selectedCommune.ville} ({selectedCommune.codePostal})</p>
                <p><strong>Population :</strong> {selectedCommune.nombreHabitants.toLocaleString()} habitants</p>
                <p><strong>Département :</strong> {selectedCommune.departement}</p>
                <p><strong>Statut :</strong> {selectedCommune.statut}</p>
                {selectedCommune.telephone && <p><strong>Téléphone :</strong> {selectedCommune.telephone}</p>}
                {selectedCommune.email && <p><strong>Email :</strong> {selectedCommune.email}</p>}
              </div>
            </div>

            <div className="map-section">
              <ItineraireMap
                selectedCommune={selectedCommune}
                itineraire={itineraire}
              />
            </div>

            <div className="list-section">
              <ItineraireList itineraire={itineraire} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
